export const CMS_DOMAIN = 'https://manager.hyundaicolombia.co';

export interface ImageSizes {
    mobile: string;   // 480px
    tablet: string;   // 768px
    desktop: string;  // 1280px
}

export type ImageStrategy = 'none' | 'params' | 'cloudflare' | 'cloudinary';

let currentStrategy: ImageStrategy = 'params';
let CLOUDINARY_CLOUD_NAME = 'hyundai-colombia';

/**
 * Configura la estrategia de optimización de imágenes.
 */
export const setImageStrategy = (strategy: ImageStrategy) => {
    currentStrategy = strategy;
};

/**
 * Configura el cloud name de Cloudinary si se usa esa estrategia.
 */
export const setCloudinaryCloudName = (cloudName: string) => {
    CLOUDINARY_CLOUD_NAME = cloudName;
};

export const cleanCmsUrl = (path: string): string => {
    if (!path) return '';
    // Eliminar slash inicial y reemplazar múltiples slashes por uno solo
    return path.replace(/^\/+/, '').replace(/\/+/g, '/');
};

export const buildCmsUrl = (path: string): string => {
    return `${CMS_DOMAIN}/${cleanCmsUrl(path)}`;
};

/**
 * Genera una URL optimizada usando parámetros de consulta (Solución actual).
 */
export const getParamsImageUrl = (path: string, width: number, quality: number = 60, format: string = 'webp'): string => {
    const cleanPath = cleanCmsUrl(path);
    return `${CMS_DOMAIN}/${cleanPath}?width=${width}&quality=${quality}&format=${format}`;
};

/**
 * Genera una URL usando Cloudflare Images (cdn-cgi/image/).
 */
export const getCloudflareImageUrl = (path: string, width: number, quality: number = 60, format: string = 'webp'): string => {
    const cleanPath = cleanCmsUrl(path);
    return `${CMS_DOMAIN}/cdn-cgi/image/width=${width},quality=${quality},format=${format}/${cleanPath}`;
};

/**
 * Genera una URL usando Cloudinary Fetch API.
 */
export const getCloudinaryUrl = (path: string, width: number, quality: number = 60, format: string = 'auto'): string => {
    const cleanPath = cleanCmsUrl(path);
    const fullUrl = `${CMS_DOMAIN}/${cleanPath}`;
    return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/fetch/f_${format},q_${quality},w_${width}/${fullUrl}`;
};

/**
 * Función principal que despacha a la estrategia configurada.
 */
export const getOptimizedUrl = (path: string, width: number, quality: number = 60, format: string = 'webp'): string => {
    if (!path) return '';

    // Si es un SVG, no optimizar (mantener vector original)
    if (path.toLowerCase().endsWith('.svg')) {
        return buildCmsUrl(path);
    }

    switch (currentStrategy) {
        case 'cloudflare':
            return getCloudflareImageUrl(path, width, quality, format);
        case 'cloudinary':
            return getCloudinaryUrl(path, width, quality, format);
        case 'params':
            return getParamsImageUrl(path, width, quality, format);
        case 'none':
        default:
            return buildCmsUrl(path);
    }
};

export const getResponsiveImage = (basePath: string): ImageSizes => {
    return {
        mobile: getOptimizedUrl(basePath, 480),
        tablet: getOptimizedUrl(basePath, 768),
        desktop: getOptimizedUrl(basePath, 1280),
    };
};

export const getSrcSet = (basePath: string): string => {
    if (!basePath) return '';

    // Si es un SVG, no generar srcset
    if (basePath.toLowerCase().endsWith('.svg')) {
        return '';
    }

    return `
    ${getOptimizedUrl(basePath, 480)} 480w,
    ${getOptimizedUrl(basePath, 768)} 768w,
    ${getOptimizedUrl(basePath, 1280)} 1280w
  `.trim();
};

export const getSizes = (defaultSize: string = '100vw'): string => {
    return `
    (max-width: 480px) 480px,
    (max-width: 768px) 768px,
    ${defaultSize}
  `.trim();
};